<template>
  <div class="student-layout d-flex flex-column min-vh-100">
    <!-- Top Navbar -->
    <nav class="navbar navbar-expand-lg navbar-dark app-navbar shadow-sm">
      <div class="container-fluid px-4">
        <div class="d-flex align-items-center gap-2">
          <div class="brand-icon">
            <i class="bi bi-mortarboard-fill"></i>
          </div>
          <span class="navbar-brand fw-bold mb-0">LearnBridge</span>
        </div>

        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#studentNavbar"
          aria-controls="studentNavbar"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <div class="collapse navbar-collapse" id="studentNavbar">
          <ul class="navbar-nav me-auto ms-lg-4 mt-3 mt-lg-0">
            <li class="nav-item">
              <router-link
                :to="{ name: 'studentDashboard' }"
                class="nav-link"
              >
                Home
              </router-link>
            </li>

            <li class="nav-item">
              <router-link
                :to="{ name: 'start-practice' }"
                class="nav-link"
              >
                Practice
              </router-link>
            </li>

            <li class="nav-item">
              <a href="#" class="nav-link">Profile</a>
            </li>

            <li class="nav-item">
              <a
                href="#"
                class="nav-link"
                @click.prevent="showJoinModal = true"
              >
                Join a Class
              </a>
            </li>
          </ul>

          <div class="navbar-nav ms-auto d-flex align-items-lg-center">
            <span class="student-chip text-white me-lg-3 mb-2 mb-lg-0">
              <i class="bi bi-person-circle me-2"></i>
              Student
            </span>

            <button
              class="btn btn-light btn-sm logout-btn"
              @click="logout"
              title="Logout"
            >
              <i class="bi bi-box-arrow-right me-1"></i>
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>

    <div class="layout-body flex-grow-1 d-flex">
      <!-- Sidebar -->
      <aside class="student-sidebar d-flex flex-column">
        <div class="sidebar-header">
          <div class="sidebar-title">Student Menu</div>
          <div class="sidebar-subtitle">Practice, learn, and grow</div>
        </div>

        <ul class="nav flex-column sidebar-nav px-3 py-3 gap-2">
          <li class="nav-item">
            <router-link
              :to="{ name: 'studentDashboard' }"
              active-class="active"
              class="nav-link sidebar-link"
            >
              <i class="bi bi-speedometer2"></i>
              <span>Dashboard</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link
              :to="{ name: 'start-practice' }"
              active-class="active"
              class="nav-link sidebar-link"
            >
              <i class="bi bi-pencil-square"></i>
              <span>Practice</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link
              :to="{ name: 'StudentPracticeList' }"
              active-class="active"
              class="nav-link sidebar-link"
            >
              <i class="bi bi-clipboard-check"></i>
              <span>My Assessments</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link
              to="/practice-papers"
              active-class="active"
              class="nav-link sidebar-link"
            >
              <i class="bi bi-journal-text"></i>
              <span>Practice Papers</span>
            </router-link>
          </li>

          <li class="nav-item">
            <a href="#" class="nav-link sidebar-link">
              <i class="bi bi-gear"></i>
              <span>Settings</span>
            </a>
          </li>

          <li class="nav-item">
            <a
              href="#"
              class="nav-link sidebar-link join-link"
              @click.prevent="showJoinModal = true"
            >
              <i class="bi bi-plus-circle"></i>
              <span>Join a Class</span>
            </a>
          </li>
        </ul>

        <!-- My Classes -->
        <div class="classes-section px-3 pb-3">
          <div class="section-label">
            <i class="bi bi-people-fill me-2"></i>
            My Classes
          </div>

          <div v-if="groups.length > 0" class="class-list">
            <router-link
              v-for="group in groups"
              :key="group.id"
              :to="{ name: 'student-group', params: { id: group.id } }"
              class="class-item"
            >
              <span class="class-icon">
                <i class="bi bi-collection"></i>
              </span>
              <span class="class-name">{{ group.group_name }}</span>
            </router-link>
          </div>

          <div v-else class="empty-classes">
            <i class="bi bi-info-circle me-1"></i>
            No classes joined yet.
          </div>
        </div>

        <div class="sidebar-footer mt-auto px-3 pb-4">
          <div class="study-card">
            <i class="bi bi-stars"></i>
            <div>
              <div class="fw-semibold">Keep practicing</div>
              <small>Small progress every day matters.</small>
            </div>
          </div>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="main-content flex-grow-1">
        <div class="container-fluid">
          <router-view />
        </div>
      </main>
    </div>

    <!-- Join Class Modal -->
    <div
      v-if="showJoinModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(15, 23, 42, 0.55)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content join-modal">
          <div class="modal-header">
            <div>
              <h5 class="modal-title fw-bold">Join Class</h5>
              <small class="text-muted">
                Enter the class code shared by your teacher.
              </small>
            </div>

            <button
              type="button"
              class="btn-close"
              @click="closeJoinModal"
            ></button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="joinClass">
              <div class="mb-3">
                <label for="classCode" class="form-label fw-semibold">
                  Class Code
                </label>

                <div class="input-group">
                  <span class="input-group-text">
                    <i class="bi bi-key"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    id="classCode"
                    v-model="classCode"
                    placeholder="Enter the class code"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                class="btn btn-primary join-btn"
                :disabled="joining"
              >
                <span
                  v-if="joining"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                <i v-else class="bi bi-box-arrow-in-right me-1"></i>
                {{ joining ? "Joining..." : "Join Class" }}
              </button>
            </form>

            <div
              v-if="joinMessage"
              class="mt-3 alert"
              :class="joinSuccess ? 'alert-success' : 'alert-danger'"
            >
              {{ joinMessage }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "StudentLayout",

  data() {
    return {
      showJoinModal: false,
      classCode: "",
      joining: false,
      joinMessage: "",
      joinSuccess: false,
      groups: [],
    };
  },

  async created() {
    await this.loadGroups();
  },

  methods: {
    async loadGroups() {
      try {
        const token = localStorage.getItem("auth_token");

        const response = await axios.get("/my-groups", {
          headers: { Authorization: `Bearer ${token}` },
        });

        this.groups = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        console.error("Failed to load groups:", err);
        this.groups = [];
      }
    },

    closeJoinModal() {
      this.showJoinModal = false;
      this.classCode = "";
      this.joinMessage = "";
      this.joinSuccess = false;
    },

    async joinClass() {
      this.joining = true;
      this.joinMessage = "";

      try {
        const token = localStorage.getItem("auth_token");

        const response = await axios.post(
          "/groups/join",
          {
            class_code: this.classCode,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        this.joinSuccess = true;
        this.joinMessage = response.data.message || "Class joined successfully.";

        await this.loadGroups();

        setTimeout(() => {
          this.closeJoinModal();
        }, 2000);
      } catch (err) {
        this.joinSuccess = false;
        this.joinMessage =
          err.response?.data?.message || "Failed to join class.";
      } finally {
        this.joining = false;
      }
    },

    async logout() {
      const token = localStorage.getItem("auth_token");

      try {
        await axios.post(
          "/logout",
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (e) {
        // Continue logout locally even if the backend logout request fails.
      }

      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_role");
      delete axios.defaults.headers.common["Authorization"];

      this.$router.push({ name: "login" });
    },
  },
};
</script>

<style scoped>
.student-layout {
  background: #f4f7fb;
}

/* Top Navbar */
.app-navbar {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  min-height: 64px;
}

.navbar-brand {
  letter-spacing: 0.2px;
}

.brand-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.student-chip {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  width: fit-content;
}

.logout-btn {
  border-radius: 999px;
  font-weight: 700;
  color: #2563eb;
  border: none;
  padding-left: 0.9rem;
  padding-right: 0.9rem;
}

.logout-btn:hover {
  background: #eef2ff;
  color: #1d4ed8;
}

/* Layout Body */
.layout-body {
  background: #f4f7fb;
}

/* Sidebar */
.student-sidebar {
  width: 270px;
  min-height: calc(100vh - 64px);
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  box-shadow: 4px 0 18px rgba(15, 23, 42, 0.05);
}

.sidebar-header {
  padding: 1.35rem 1.25rem 0.8rem;
  border-bottom: 1px solid #eef2f7;
}

.sidebar-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #111827;
}

.sidebar-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.sidebar-nav {
  margin: 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #475569;
  font-weight: 700;
  border-radius: 14px;
  padding: 0.78rem 0.9rem;
  transition: all 0.2s ease;
}

.sidebar-link i {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
}

.sidebar-link:hover {
  background: #f1f5ff;
  color: #4f46e5;
  transform: translateX(2px);
}

.sidebar-link.active {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4338ca !important;
  box-shadow: inset 3px 0 0 #4f46e5;
}

.sidebar-link.active i {
  color: #4f46e5;
}

.join-link {
  color: #2563eb;
}

.join-link:hover {
  color: #1d4ed8;
}

/* Classes */
.classes-section {
  border-top: 1px solid #eef2f7;
  padding-top: 1rem;
}

.section-label {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.75rem;
}

.class-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.class-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  color: #334155;
  transition: all 0.2s ease;
}

.class-item:hover {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #4338ca;
}

.class-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: #e0e7ff;
  color: #4f46e5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.class-name {
  font-size: 0.85rem;
  font-weight: 700;
}

.empty-classes {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  border-radius: 12px;
  padding: 0.75rem;
  font-size: 0.82rem;
}

/* Footer Card */
.sidebar-footer {
  border-top: 1px solid #eef2f7;
  padding-top: 1rem;
}

.study-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 0.85rem;
  color: #475569;
}

.study-card i {
  color: #f59e0b;
  font-size: 1.1rem;
}

.study-card small {
  color: #64748b;
}

/* Main Content */
.main-content {
  padding: 1.5rem;
  min-width: 0;
}

/* Modal */
.join-modal {
  border: none;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
}

.join-modal .modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
}

.input-group-text {
  background: #f8fafc;
  color: #4f46e5;
  border-color: #dee2e6;
}

.form-control {
  border-color: #dee2e6;
}

.form-control:focus {
  border-color: #818cf8;
  box-shadow: 0 0 0 0.2rem rgba(99, 102, 241, 0.15);
}

.join-btn {
  border-radius: 999px;
  font-weight: 700;
  padding-left: 1rem;
  padding-right: 1rem;
}

/* Mobile */
@media (max-width: 992px) {
  .layout-body {
    flex-direction: column;
  }

  .student-sidebar {
    width: 100%;
    min-height: auto;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .sidebar-header,
  .sidebar-footer {
    display: none;
  }

  .sidebar-nav {
    flex-direction: row !important;
    overflow-x: auto;
    white-space: nowrap;
    padding: 0.75rem !important;
  }

  .sidebar-nav .nav-item {
    flex: 0 0 auto;
  }

  .sidebar-link {
    padding: 0.65rem 0.85rem;
  }

  .sidebar-link:hover {
    transform: none;
  }

  .classes-section {
    padding: 0.75rem 1rem 1rem;
  }

  .class-list {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 0.25rem;
  }

  .class-item {
    flex: 0 0 auto;
  }

  .main-content {
    padding: 1rem;
  }
}

@media (max-width: 576px) {
  .container-fluid {
    padding-left: 0;
    padding-right: 0;
  }

  .navbar .container-fluid {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .navbar-brand {
    font-size: 1rem;
  }

  .brand-icon {
    width: 34px;
    height: 34px;
    font-size: 1rem;
  }

  .logout-btn {
    width: fit-content;
  }
}
</style>